import React, { memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  aribaPasswordAdapterChoices,
  createAribaSyncPair,
  getSyncPairsAriba,
  updateAribaSyncPair,
} from '~/api/evisync';
import ProviderLogo from '~/components/Shared/ProviderLogo/ProviderLogo';
import { Chips } from '~/eds/src/components/Chips/Chips';
import { DateText } from '~/eds/src/components/DateText/DateText';
import { Divider } from '~/eds/src/components/Divider/Divider';
import { Filename } from '~/eds/src/components/Filename/Filename';
import { Grid } from '~/eds/src/components/Grid/Grid';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { AribaIntegrationType } from '~/enums/AribaIntegrationType';
import { ManageSyncPairStageType } from '~/enums/ManageSyncPairStageType';
import { useAsync } from '~/hooks/useAsync';
import { useIntl } from 'react-intl';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import Folder from '~/ui/components/Folder/Folder';
import { fileTypesToChips } from '~/utils/fileTypesToChips';

import { CONTRACT_WORKSPACE_EVISORT_VIEW, SYNC_SYMBOL } from '../../constants';
import { resetSyncPairAuthData } from '../../util';
import AribaFooter from '../AribaFooter';
import SummaryRowAriba from './SummaryRowAriba';

function SummaryAriba() {
  const dispatch = useDispatch();
  const intl = useIntl();
  const activeSyncPair = useSelector(
    slices.integrations.selectors.selectActiveSyncPair,
  );

  const { provider, syncType, syncPair, allowedSyncFileTypes } = activeSyncPair;
  const { locationName, realm } = activeSyncPair[ManageSyncPairStageType.Realm];
  const { evisortFolder } = activeSyncPair[ManageSyncPairStageType.FolderAriba];
  const fieldsMappingStage = ManageSyncPairStageType.FieldMapping;
  const docIdsFile =
    activeSyncPair[ManageSyncPairStageType.FolderAriba].docIdsFile ?? [];
  const cwCount =
    activeSyncPair[ManageSyncPairStageType.FolderAriba].cwCount ?? 'Unknown';
  const sinceDate =
    activeSyncPair[ManageSyncPairStageType.FolderAriba].sinceDate;
  const fieldsMapping =
    activeSyncPair[fieldsMappingStage].fieldsMapping.filter(
      (field) => field.evisort && field.ariba && field.sync,
    ) ?? [];
  const fieldMappingFilePath =
    activeSyncPair[fieldsMappingStage].fieldMappingFilePath ?? null;
  const taskDiscovery = activeSyncPair.taskDiscovery ?? { enabled: false };

  const { navigate, params } = useRouting();

  const { clientId } = params;

  const formatSyncPair = () => {
    const updatedSyncPair = {
      id: syncPair,
      provider,
      providerFolderPath: CONTRACT_WORKSPACE_EVISORT_VIEW,
      folderId: evisortFolder.folderId,
      allowedSyncFileTypes: allowedSyncFileTypes,
      mapping: fieldsMapping,
      extraContext: { taskDiscovery },
      syncType,
    };
    if (fieldMappingFilePath) {
      updatedSyncPair.fieldMappingFilePath = fieldMappingFilePath;
    }
    if (syncType === AribaIntegrationType.CONTINUOUS) {
      updatedSyncPair.sinceDate = sinceDate?.getTime();
    } else {
      updatedSyncPair.providerOptions = { file: docIdsFile[0] };
    }
    return updatedSyncPair;
  };

  const successHandlerSyncPair = () => {
    getSyncPairsAriba({ clientId }).then((pairs) =>
      dispatch(slices.integrations.actions.setSyncPairs(pairs)),
    );
    dispatch(
      slices.integrations.actions.setManageSyncPairStage(
        ManageSyncPairStageType.None,
      ),
    );
    navigate(`/admin/console/${clientId}/integrations?tab=procurement`);
  };

  const { isLoading: isCreating, executor: executeCreate } = useAsync(
    createAribaSyncPair,
    formatSyncPair(),
    {
      errorToastMessage: intl.formatMessage({
        id: 'SummaryAriba.createSyncPairError',
        defaultMessage: 'Unable to create sync pair, please try again.',
        description: 'Error message when creating sync pair fails',
      }),
      successHandler: () => {
        successHandlerSyncPair();
      },
    },
  );

  const { isLoading: isUpdating, executor: executeUpdate } = useAsync(
    updateAribaSyncPair,
    formatSyncPair(),
    {
      errorToastMessage: intl.formatMessage({
        id: 'SummaryAriba.updateSyncPairError',
        defaultMessage: 'Unable to create sync pair, please try again.',
        description: 'Error message when updating sync pair fails',
      }),
      successHandler: () => {
        successHandlerSyncPair();
      },
    },
  );

  const handleCreateSyncPair = () => {
    if (syncType === AribaIntegrationType.ONE_TIME) {
      executeCreate();
    } else {
      executeUpdate();
    }
  };

  const handleGoToStage = (stage) => () => {
    dispatch(slices.integrations.actions.setManageSyncPairStage(stage));
    dispatch(
      slices.integrations.actions.setActiveSyncPair(
        resetSyncPairAuthData(activeSyncPair),
      ),
    );
  };

  const handleBack = () => {
    dispatch(
      slices.integrations.actions.setManageSyncPairStage(
        ManageSyncPairStageType.FieldMapping,
      ),
    );
  };

  const getEvisortFieldLabel = (metaData) => {
    return metaData.evisortField ? metaData.evisortField.label : null;
  };

  const getAribaFieldLabel = (metaData) => {
    return metaData.aribaField ? metaData.aribaField.fieldName : null;
  };

  const fieldsData = fieldsMapping.map((field, index) => {
    return {
      evisortField: `${index + 1}. ${getEvisortFieldLabel(field.meta)}`,
      sync: SYNC_SYMBOL[field.sync],
      aribaField: getAribaFieldLabel(field.meta),
    };
  });

  const fieldsColumns = [
    {
      label: intl.formatMessage({
        id: 'SummaryAriba.evisortFieldColumn',
        defaultMessage: 'Evisort Field',
        description: 'Column header for Evisort fields',
      }),
      key: 'evisortField',
    },
    {
      label: intl.formatMessage({
        id: 'SummaryAriba.syncColumn',
        defaultMessage: 'Sync',
        description: 'Column header for sync direction',
      }),
      key: 'sync',
      props: { align: 'center' },
    },
    {
      label: intl.formatMessage({
        id: 'SummaryAriba.aribaFieldColumn',
        defaultMessage: 'Ariba Field',
        description: 'Column header for Ariba fields',
      }),
      key: 'aribaField',
    },
  ];

  const FieldList = memo(({ columns, data }) => {
    const fields = data.map((datum) => {
      const inputs = columns.map(({ key, props }) => {
        const value = datum[key];
        return (
          <Text key={key} style={{ flex: 1 }} {...props}>
            {value}
          </Text>
        );
      });

      return inputs;
    });

    return (
      <Grid
        columns={columns.length}
        columnSpacing={2}
        rowSpacing={2}
        columnWidths={['8fr', '1fr', '8fr']}
      >
        {fields}
      </Grid>
    );
  });

  return (
    <Layout align="center" direction="column" spacing={8} w="100%">
      <Layout align="center">
        <ProviderLogo provider={provider} />
      </Layout>
      <Divider />
      <Layout direction="column" spacing={8} w="90%">
        <SummaryRowAriba
          title={intl.formatMessage({
            id: 'SummaryAriba.realmIdTitle',
            defaultMessage: 'Realm ID',
            description: 'Title for realm ID section',
          })}
        >
          <Text>{realm}</Text>
        </SummaryRowAriba>
        <SummaryRowAriba
          title={intl.formatMessage({
            id: 'SummaryAriba.dataCenterLocationTitle',
            defaultMessage: 'Data Center Location',
            description: 'Title for data center location section',
          })}
        >
          <Text>{locationName}</Text>
        </SummaryRowAriba>
        <SummaryRowAriba
          title={intl.formatMessage({
            id: 'SummaryAriba.evisortFolderTitle',
            defaultMessage: 'Evisort Folder',
            description: 'Title for Evisort folder section',
          })}
          onChange={handleGoToStage(ManageSyncPairStageType.FolderAriba)}
        >
          <Folder
            folder={evisortFolder}
            type="internal"
            shouldTruncate={true}
          />
        </SummaryRowAriba>
        <SummaryRowAriba
          title={intl.formatMessage({
            id: 'SummaryAriba.fileTypesTitle',
            defaultMessage: 'File Types',
            description: 'Title for file types section',
          })}
          onChange={handleGoToStage(ManageSyncPairStageType.FolderAriba)}
        >
          <Chips chips={fileTypesToChips(allowedSyncFileTypes)} limit={100} />
        </SummaryRowAriba>
        {syncType === AribaIntegrationType.ONE_TIME ? (
          <SummaryRowAriba
            title={intl.formatMessage({
              id: 'SummaryAriba.aribaDocumentIdsTitle',
              defaultMessage: 'Ariba Document IDs',
              description: 'Title for Ariba document IDs section',
            })}
            onChange={handleGoToStage(ManageSyncPairStageType.FolderAriba)}
          >
            <Filename name={docIdsFile[0].name} />
          </SummaryRowAriba>
        ) : null}
        {syncType === AribaIntegrationType.CONTINUOUS ? (
          <SummaryRowAriba
            title={intl.formatMessage({
              id: 'SummaryAriba.dateFilterTitle',
              defaultMessage: 'Date filter',
              description: 'Title for date filter section',
            })}
            onChange={handleGoToStage(ManageSyncPairStageType.FolderAriba)}
          >
            <DateText format="short" date={sinceDate} />
          </SummaryRowAriba>
        ) : null}

        {syncType === AribaIntegrationType.CONTINUOUS ? (
          <SummaryRowAriba
            title={intl.formatMessage({
              id: 'SummaryAriba.useAribaTaskTitle',
              defaultMessage: 'Use Ariba Task for Faster Sync',
              description: 'Title for Ariba task usage section',
            })}
            onChange={handleGoToStage(ManageSyncPairStageType.FolderAriba)}
          >
            <Text>
              {taskDiscovery.enabled
                ? intl.formatMessage({
                    id: 'SummaryAriba.yesLabel',
                    defaultMessage: 'Yes',
                    description: 'Yes label',
                  })
                : intl.formatMessage({
                    id: 'SummaryAriba.noLabel',
                    defaultMessage: 'No',
                    description: 'No label',
                  })}
            </Text>
          </SummaryRowAriba>
        ) : null}
        {syncType === AribaIntegrationType.CONTINUOUS ? (
          <SummaryRowAriba
            title={intl.formatMessage({
              id: 'SummaryAriba.aribaTaskUserTitle',
              defaultMessage: 'Ariba Task User',
              description: 'Title for Ariba task user section',
            })}
            onChange={handleGoToStage(ManageSyncPairStageType.FolderAriba)}
          >
            <Text>{taskDiscovery.approverId || '--'}</Text>
          </SummaryRowAriba>
        ) : null}
        {syncType === AribaIntegrationType.CONTINUOUS ? (
          <SummaryRowAriba
            title={intl.formatMessage({
              id: 'SummaryAriba.aribaTaskUserAuthTitle',
              defaultMessage: 'Ariba Task User Authentication Method',
              description:
                'Title for Ariba task user authentication method section',
            })}
            onChange={handleGoToStage(ManageSyncPairStageType.FolderAriba)}
          >
            <Text>
              {aribaPasswordAdapterChoices
                .filter((each) => each.value === taskDiscovery.passwordAdapter)
                .map((each) => each.label)
                .shift() || '--'}
            </Text>
          </SummaryRowAriba>
        ) : null}
        {syncType === AribaIntegrationType.CONTINUOUS ? (
          <SummaryRowAriba
            title={intl.formatMessage({
              id: 'SummaryAriba.contractWorkspacesTitle',
              defaultMessage: 'Contract Workspaces',
              description: 'Title for contract workspaces section',
            })}
          >
            <Text>{cwCount}</Text>
          </SummaryRowAriba>
        ) : null}
        {fieldsData ? (
          <SummaryRowAriba
            title={intl.formatMessage({
              id: 'SummaryAriba.fieldMappingsTitle',
              defaultMessage: 'List of Field Mappings',
              description: 'Title for field mappings section',
            })}
            onChange={handleGoToStage(ManageSyncPairStageType.FieldMapping)}
          >
            <Layout>
              <FieldList columns={fieldsColumns} data={fieldsData} />
            </Layout>
          </SummaryRowAriba>
        ) : null}
      </Layout>
      <AribaFooter
        secondaryAction={{
          text: intl.formatMessage({
            id: 'SummaryAriba.backButton',
            defaultMessage: 'Back',
            description: 'Back button text',
          }),
          onClick: handleBack,
        }}
        mainAction={{
          text: intl.formatMessage({
            id: 'SummaryAriba.confirmSyncPairButton',
            defaultMessage: 'Confirm Sync Pair',
            description: 'Confirm sync pair button text',
          }),
          onClick: handleCreateSyncPair,
          isLoading: isCreating || isUpdating,
        }}
      />
    </Layout>
  );
}

export default SummaryAriba;
